import type { ICommand } from "./ICommand";
import type { DRDrawingViewer } from "../DRDrawingViewer";
import type { DrawingModule } from "../types/DrawingWeb";
import { deleteObj } from "../core/delete";
import { openAs } from "../core/oda";
import { OpenMode } from "../core/constants";
import { HatchReactorManager } from "../managers/HatchReactorManager";
import { oplogEmitter, type OplogEmit } from "../meta/oplog";

export interface VertexMove {
  /** The vertex index to move */
  vertexIndex: number;
  /** The old vertex position */
  oldPoint: { x: number; y: number };
  /** The new vertex position */
  newPoint: { x: number; y: number };
}

export interface MovePolylineVertexParams {
  /** The entity handle string */
  handle: string;
  /** The vertices to move */
  vertices: VertexMove[];
}

/**
 * Command for moving one or more vertices of a polyline.
 * Used for vertex editing with undo/redo support.
 */
export class MovePolylineVertexCommand implements ICommand {
  private viewer: DRDrawingViewer;
  private handle: string;
  private vertices: VertexMove[];
  private oplogEvents: OplogEmit[] = [];

  constructor(viewer: DRDrawingViewer, params: MovePolylineVertexParams) {
    this.viewer = viewer;
    this.handle = params.handle;
    this.vertices = params.vertices;
  }

  /**
   * Move vertices to new positions (already done during drag - this is a no-op on
   * first execute besides the oplog emission). Les hachures réactives sur ce contour
   * (`HatchReactorManager`) sont resynchronisées silencieusement, sans event dédié.
   */
  execute(): void {
    this.oplogEvents = oplogEmitter.emitModified(this.viewer, [
      { handle: this.handle, changeKind: "moved" },
    ]);
    this.setVertexPositions((v) => v.newPoint);
    this.viewer.update();
  }

  /** Move vertices back to old positions */
  undo(): void {
    this.setVertexPositions((v) => v.oldPoint);
    this.viewer.update();
    oplogEmitter.revert(this.oplogEvents);
  }

  /** Move vertices to new positions */
  redo(): void {
    this.setVertexPositions((v) => v.newPoint);
    this.viewer.update();
    oplogEmitter.reemit(this.oplogEvents);
  }

  /** No special cleanup needed */
  dispose(): void {
    // Nothing to dispose
  }

  private setVertexPositions(
    getPoint: (v: VertexMove) => { x: number; y: number },
  ): void {
    const entityId = this.getEntityIdFromHandle();
    if (!entityId) return;

    const polyline = openAs(
      entityId,
      this.viewer.module.OdDbPolyline,
      OpenMode.kForWrite,
    );

    for (const vertex of this.vertices) {
      const point = getPoint(vertex);
      const point2d = new this.viewer.module.OdGePoint2d(point.x, point.y);
      polyline.setPointAt(vertex.vertexIndex, point2d);
      deleteObj(point2d);
    }

    // Mark entity as graphically modified
    polyline.recordGraphicsModified(true);

    // Sync hatches that are reactors on this polyline
    HatchReactorManager.getInstance(this.viewer.module).syncHatchesForBoundary(
      entityId,
    );
  }

  private getEntityIdFromHandle(): DrawingModule.OdDbObjectId | null {
    const db = this.viewer.database;
    if (!db) return null;

    const module = this.viewer.module;
    const handleStr = new module.OdString(this.handle);
    const handle = module.OdDbHandle.ctr2(handleStr);
    const entityId = db.getOdDbObjectId(handle, false, 0);
    deleteObj(handleStr);
    deleteObj(handle);

    if (entityId && !entityId.isNull()) {
      return entityId;
    }
    return null;
  }
}
